import type { AtAGlanceBucket, AtAGlanceBucketSize } from "@insights/contracts";
import { Card, Combobox } from "@insights/ui";
import { GitPullRequest, Hammer, Rocket, TimerReset } from "lucide-react";
import { useParams, useSearchParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { AtAGlanceMetricCard } from "../components/AtAGlanceMetricCard";
import {
  bucketLabel,
  formatDuration,
  normalizeBucketSize,
  percentageChange,
  savingsPercentage,
} from "../helpers/at-a-glance-format";
import { useAtAGlance, type ActivityFilters } from "../hooks/useAtAGlance";
import "../styles/at-a-glance.css";

const bucketOptions = [
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
] as const;

function readFilters(searchParams: URLSearchParams): ActivityFilters {
  return {
    bucketSize: normalizeBucketSize(searchParams.get("bucket")),
    repository: searchParams.get("repository") ?? "",
    teamId: searchParams.get("teamId") ?? "",
    contributor: searchParams.get("contributor") ?? "",
  };
}

function sum(
  buckets: AtAGlanceBucket[],
  field: keyof AtAGlanceBucket,
) {
  return buckets.reduce((total, bucket) => {
    const value = bucket[field];
    return total + (typeof value === "number" ? value : 0);
  }, 0);
}

function periodName(size: AtAGlanceBucketSize) {
  return size === "week" ? "week" : "month";
}

export function AtAGlancePage() {
  const { workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);
  const bucketSize = filters.bucketSize;
  const dashboard = useAtAGlance(workspaceId, filters);

  function updateFilters(update: Partial<ActivityFilters>) {
    const next = {
      ...filters,
      ...update,
    };
    const params = new URLSearchParams({ bucket: next.bucketSize });

    if (next.repository) {
      params.set("repository", next.repository);
    }
    if (next.teamId) {
      params.set("teamId", next.teamId);
    }
    if (next.contributor) {
      params.set("contributor", next.contributor);
    }

    setSearchParams(params);
  }

  const buckets = dashboard.data?.buckets ?? [];
  const latest = buckets.at(-1);
  const previous = buckets.at(-2);
  const labels = buckets.map((bucket) => bucketLabel(bucket.from, bucketSize));
  const period = periodName(bucketSize);

  const countCards = latest && previous
    ? [
        {
          chartLabel: `Pull requests by ${period}`,
          field: "pullRequests" as const,
          icon: <GitPullRequest />,
          title: "Pull requests",
        },
        {
          chartLabel: `Builds by ${period}`,
          field: "builds" as const,
          icon: <Hammer />,
          title: "Builds",
        },
        {
          chartLabel: `Releases by ${period}`,
          field: "releases" as const,
          icon: <Rocket />,
          title: "Releases",
        },
      ]
    : [];

  return (
    <AppShell>
      <PageHeader
        eyebrow="Pulse"
        title="Activity"
        description="Completed activity periods across selected repositories. The current period is excluded."
      />

      <div className="activity-filters" aria-label="Activity filters">
        <Combobox
          label="Period"
          options={bucketOptions}
          value={bucketSize}
          onValueChange={(value: AtAGlanceBucketSize) => {
            updateFilters({ bucketSize: value });
          }}
        />
        <Combobox
          label="Repository"
          options={[
            { value: "all", label: "All selected repositories" },
            ...(dashboard.data?.scopeOptions.repositories ?? []).map(
              (repository) => ({
                value: repository,
                label: repository,
              }),
            ),
          ]}
          value={filters.repository || "all"}
          onValueChange={(value) => {
            updateFilters({ repository: value === "all" ? "" : value });
          }}
        />
        <Combobox
          label="Team"
          options={[
            { value: "all", label: "All teams" },
            ...(dashboard.data?.scopeOptions.teams ?? []).map((team) => ({
              value: team.id,
              label: team.name,
            })),
          ]}
          value={filters.teamId || "all"}
          onValueChange={(value) => {
            updateFilters({
              teamId: value === "all" ? "" : value,
              contributor: "",
            });
          }}
        />
        <Combobox
          label="Contributor"
          options={[
            { value: "all", label: "All contributors" },
            ...(dashboard.data?.scopeOptions.contributors ?? []).map(
              (contributor) => ({
                value: contributor,
                label: `@${contributor}`,
              }),
            ),
          ]}
          value={filters.contributor || "all"}
          onValueChange={(value) => {
            updateFilters({ contributor: value === "all" ? "" : value });
          }}
        />
      </div>

      {dashboard.isLoading && <Loading />}
      {dashboard.isError && (
        <Card className="glance-error" role="alert">
          Unable to load activity metrics.
        </Card>
      )}
      {dashboard.data?.scopeNote && (
        <p className="activity-scope-note">{dashboard.data.scopeNote}</p>
      )}
      {latest && previous && (
        <div className="glance-metric-grid">
          {countCards.map((card) => (
            <AtAGlanceMetricCard
              chartLabel={card.chartLabel}
              chartPoints={buckets.map((bucket, index) => ({
                label: labels[index]!,
                value: bucket[card.field],
              }))}
              change={percentageChange(latest[card.field], previous[card.field])}
              eyebrow={`Last completed ${period}`}
              formatValue={(value) => Math.round(value).toLocaleString()}
              icon={card.icon}
              key={card.field}
              title={card.title}
              total={sum(buckets, card.field)}
              value={latest[card.field]}
            />
          ))}
          <AtAGlanceMetricCard
            chartLabel={`Estimated build time saved by ${period}`}
            chartPoints={buckets.map((bucket, index) => ({
              label: labels[index]!,
              value: bucket.savedBuildTimeMs,
            }))}
            change={percentageChange(
              latest.savedBuildTimeMs,
              previous.savedBuildTimeMs,
            )}
            eyebrow={`Last completed ${period}`}
            formatValue={formatDuration}
            icon={<TimerReset />}
            supporting={(
              <div className="glance-gain-detail">
                <span>
                  {formatDuration(latest.actualBuildTimeMs)} actual build time
                </span>
                <span>
                  {savingsPercentage(
                    latest.savedBuildTimeMs,
                    latest.actualBuildTimeMs,
                  ).toFixed(0)}% estimated savings
                </span>
                {buckets.some((bucket) => bucket.gainCoverage === "partial") && (
                  <p>
                    Partial coverage: legacy or unmeasured artifact reuse is
                    excluded from saved time.
                  </p>
                )}
              </div>
            )}
            title="Build time gained"
            total={sum(buckets, "savedBuildTimeMs")}
            value={latest.savedBuildTimeMs}
          />
        </div>
      )}
    </AppShell>
  );
}
